import React, { useState, useEffect } from 'react';
import { Button, Alert, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { autoWebViewJs } from '@droid/android';

interface AddSecurityWidgetButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const AddSecurityWidgetButton: React.FC<AddSecurityWidgetButtonProps> = ({ 
  onSuccess, 
  onError 
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [deviceManufacturer, setDeviceManufacturer] = useState<string>('');
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  // 获取设备厂商信息
  useEffect(() => {
    try {
      const manufacturerScript = `
var manufacturer = com.fanfanlo.emergencycall.utils.WidgetUtils.getDeviceManufacturerName();
manufacturer;`;
      
      const { javaResultString } = autoWebViewJs.callScript(manufacturerScript);
      setDeviceManufacturer(javaResultString || '通用设备');
    } catch (error) {
      console.warn('获取设备厂商信息失败:', error);
      setDeviceManufacturer('通用设备');
    }
  }, []);

  const executeAddWidget = async () => {
    const addScript = `
var success = com.fanfanlo.emergencycall.utils.WidgetUtils.requestAddSecurityWidget();
success;`;

    const { javaResultString: addResult } = autoWebViewJs.callScript(addScript);
    const addSuccess = addResult === "true";

    if (addSuccess) {
      setMessage('选择界面已打开，请选择默认样式或自定义设置');
      setMessageType('success');
      onSuccess?.();
    } else {
      throw new Error('启动选择界面失败');
    }
  };

  const handleAddWidget = async () => {
    try {
      setIsAdding(true);
      setMessage('正在检查设备支持...');
      setMessageType('info');

      // 先检查设备权限需求
      const permissionScript = `
var permissionInfo = com.fanfanlo.emergencycall.utils.WidgetUtils.getDevicePermissionInfo();
permissionInfo;`;
      
      const { javaResultString: permissionResult } = autoWebViewJs.callScript(permissionScript);
      const permissionInfo = JSON.parse(permissionResult || '{}');
      
      if (permissionInfo.needsPermission) {
        // 需要权限的设备，显示弹窗
        setMessage(`您的手机需要打开设置页面，找到 "桌面快捷方式" 的权限，并进行授权`);
        setMessageType('info');
        setShowPermissionGuide(true);
      } else {
        // 不需要权限的设备，直接添加
        await executeAddWidget();
      }
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '添加桌面安全呼救失败';
      setMessage(errorMsg);
      setMessageType('error');
      onError?.(errorMsg);
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenSettings = async () => {
    try {
      const openSettingsScript = `
var success = com.fanfanlo.emergencycall.utils.WidgetUtils.openPermissionSettings();
success;`;
      
      autoWebViewJs.callScript(openSettingsScript);
    } catch (error) {
      console.error('打开权限设置失败:', error);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Button
        variant="outlined"
        color="secondary"
        size="large"
        fullWidth
        // startIcon={<SecurityIcon />}
        onClick={handleAddWidget}
        disabled={isAdding}
        sx={{
          py: 1.5,
          borderColor: '#9333EA',
          color: '#9333EA',
          '&:hover': {
            borderColor: '#7C2D92',
            backgroundColor: 'rgba(147, 51, 234, 0.04)',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        }}
      >
        {isAdding ? '正在添加...' : '👮‍♂️ 添加安全呼救小部件（大图标）'}
      </Button>
      
      {message && (
        <Alert 
          severity={messageType} 
          sx={{ mt: 1 }}
          onClose={() => setMessage('')}
        >
          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </Typography>
        </Alert>
      )}
      
      {/* {!message && deviceManufacturer && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          当前设备：{deviceManufacturer} • 可选择默认样式或自定义设置
        </Typography>
      )} */}

      {/* 权限引导弹窗 */}
      <Dialog 
        open={showPermissionGuide} 
        onClose={() => setShowPermissionGuide(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          需要权限设置
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            检测到 {deviceManufacturer}，需要手动开启桌面快捷方式权限才能添加安全呼救小部件。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={async () => {
              setShowPermissionGuide(false);
              // 关闭弹窗后执行添加功能
              await executeAddWidget();
            }}
            variant="outlined"
          >
            已设置
          </Button>
          <Button
            onClick={() => {
              handleOpenSettings();
            }}
            variant="contained"
          >
            去设置
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddSecurityWidgetButton;